import React, { useState, useRef, useEffect } from 'react';

/**
 * MultiSelect wrapper component that provides API compatibility with
 * @khanacademy/react-multi-select while using react-select under the hood.
 */
const MultiSelect = ({
  options = [],
  selected = [],
  onSelectedChanged = () => {},
  valueRenderer,
  overrideStrings = {},
  disableSearch = false,
  hasSelectAll = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && !disableSearch) {
      searchInputRef.current.focus();
    }
  }, [isOpen, disableSearch]);

  const placeholder = overrideStrings.selectSomeItems || 'Select...';

  const formatValueLabel = () => {
    if (valueRenderer) {
      return valueRenderer(selected, options);
    }
    if (selected.length === 0) {
      return placeholder;
    }
    return `${selected.length} selected`;
  };

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleOptionClick = (optionValue) => {
    const newSelected = selected.includes(optionValue)
      ? selected.filter(v => v !== optionValue)
      : [...selected, optionValue];
    onSelectedChanged(newSelected);
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onSelectedChanged([]);
    } else {
      onSelectedChanged(options.map(opt => opt.value));
    }
  };

  return (
    <div ref={containerRef} className="multi-select-container">
      {/* Control/Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: '#fff',
          border: '1px solid rgb(78, 88, 88)',
          borderRadius: '6px',
          boxSizing: 'border-box',
          color: '#333',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: 'Montserrat-Regular, Helvetica, sans-serif',
          fontSize: '15px',
          lineHeight: '21px',
          minHeight: '46px',
          padding: '12px 10px',
          verticalAlign: 'middle',
          width: '100%',
        }}
      >
        <span style={{ color: selected.length ? '#666' : '#acb6c0' }}>
          {formatValueLabel()}
        </span>
        <span style={{ color: '#999' }}>
          ▼
        </span>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '0.555556px solid rgb(78, 88, 88)',
            borderRadius: '6px',
            marginTop: '4px',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            maxHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Montserrat-Regular, Helvetica, sans-serif',
            fontSize: '15px',
          }}
        >
          {/* Search Input */}
          {!disableSearch && (
            <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '0.555556px solid rgb(78, 88, 88)',
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                  fontFamily: 'Montserrat-Regular, Helvetica, sans-serif',
                  fontSize: '15px',
                  lineHeight: '21px',
                }}
              />
            </div>
          )}

          {/* Options List */}
          <div style={{ overflowY: 'auto', maxHeight: '220px' }}>
            {/* Select All - always first */}
            <div
              onClick={handleSelectAll}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: selected.length === options.length ? '#e6f3ff' : '#fff',
                borderBottom: '1px solid #eee',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = selected.length === options.length ? '#cce5ff' : '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = selected.length === options.length ? '#e6f3ff' : '#fff'}
            >
              <input
                type="checkbox"
                checked={selected.length === options.length && options.length > 0}
                onChange={() => {}}
                style={{ marginRight: '8px' }}
              />
              Select All
            </div>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '8px 12px', color: '#666' }}>No options</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: selected.includes(option.value) ? '#e6f3ff' : '#fff',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = selected.includes(option.value) ? '#cce5ff' : '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = selected.includes(option.value) ? '#e6f3ff' : '#fff'}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.value)}
                    onChange={() => {}}
                    style={{ marginRight: '8px' }}
                  />
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
